import { useTranslation } from 'react-i18next'
import {
    GetSustainabilityTotalEmissionsRequestParams,
    useSustainabilityApi,
} from '@/hooks'
import { Button, Icon } from '@/components/atoms'
import { MenuItem } from '@/components/common/MenuItem/MenuItem'
import { Dropdown } from '@/components/common/Dropdown/Dropdown'
import useToastStore from '@/stores/useToastStore/useToastStore'
import { useState } from 'react'

export const TotalEmissionsExportDropdown: React.FC<{
    filters: GetSustainabilityTotalEmissionsRequestParams
}> = ({ filters }) => {
    const { t } = useTranslation('translations')
    const [isOpen, setIsOpen] = useState(false)
    const addAlert = useToastStore((state) => state.addAlert)

    const { useTotalEmissionsQuery } = useSustainabilityApi()
    const { data, isFetched, downloadXlsx, isError, isFetching } =
        useTotalEmissionsQuery(filters)

    const hasData =
        Array.isArray(data?.items) &&
        data.items.length > 0 &&
        data.items.some((item) => item.value !== 0)

    const isExportButtonDisabled =
        !data?.items.length || isError || (isFetched && !hasData) || isFetching

    return (
        <Dropdown
            isOpen={isOpen}
            onClick={(event) => {
                event.preventDefault()
                event.stopPropagation()
                setIsOpen(!isOpen)
            }}
            name="total-emissions-export-dropdown"
            buttonProps={{
                name: 'total-emissions-summary-dropdown',
                children: (
                    <>
                        {t('common.export_report_dropdown.label')}
                        <Icon size="small" name="keyboard_arrow_down" />
                    </>
                ),
                variant: 'outlinePrimary',
                size: 'large',
                disabled: isExportButtonDisabled,
            }}
            buttonComponent={Button}
            horizontalAlign="right"
            dropdownClassname="py-3 !min-w-[auto]"
        >
            <MenuItem
                path="/"
                variant="ghost"
                label={t('common.export_report_dropdown.options.xls')}
                render={({ renderContents }) => (
                    <button
                        className="group w-full outline-none"
                        onClick={() => {
                            addAlert({
                                variant: 'success',
                                title: t(
                                    'reporting.export_report.success_toast.title'
                                ),
                                description: t(
                                    'reporting.export_report.success_toast.description'
                                ),
                            })

                            setIsOpen(false)

                            downloadXlsx().catch(() => {
                                addAlert({
                                    variant: 'error',
                                    title: t(
                                        'reporting.export_report.error_toast.title'
                                    ),
                                    description: t(
                                        'reporting.export_report.error_toast.description'
                                    ),
                                })
                            })
                        }}
                        data-testid="export-total-emissions-excel-button"
                    >
                        {renderContents()}
                    </button>
                )}
            />
        </Dropdown>
    )
}
